import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EmailSettingsCard from "@/components/EmailSettingsCard";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Loader2, Layers, Boxes, Activity, Gauge, ArrowRight, CheckCircle2, AlertTriangle, Server, FileText,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="card-soft p-6 hover-lift" data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div className="flex items-center justify-between mb-4">
      <Icon className="w-5 h-5" style={{ color: accent || "#3A2DB8" }} />
    </div>
    <div className="font-heading font-bold text-4xl leading-none">{value}</div>
    <div className="text-xs text-secondary mt-2">{label}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get("/dashboard").then((r) => setData(r.data)).catch((err) => { console.error("dashboard load failed", err); setData({}); }), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.running?.length) return;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [data, load]);

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#3A2DB8]" /></div>;

  const noSub = (data.active_subscriptions || 0) === 0;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="overline text-[#7C3AED] mb-2">Customer console</p>
          <h1 className="font-heading font-bold text-4xl tracking-tighter">Hello, {user?.name?.split(" ")[0] || "there"}.</h1>
        </div>
        <Button onClick={() => navigate("/config")} data-testid="dashboard-config-btn"
          className="rounded-sm bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold gap-2">
          <Server className="w-4 h-4" /> Manage environments
        </Button>
      </div>

      {noSub && (
        <div className="border border-primary/40 bg-primary/10 p-6 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="no-sub-banner">
          <div>
            <h3 className="font-heading font-semibold text-lg">Activate a testing package</h3>
            <p className="text-secondary text-sm">Subscribe to deploy autonomous agents against your SAP tenant.</p>
          </div>
          <Button onClick={() => navigate("/pricing")} className="rounded-sm bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold gap-2" data-testid="view-pricing-btn">
            View pricing <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Layers} label="Active subscriptions" value={data.active_subscriptions || 0} />
        <Stat icon={Boxes} label="Environments" value={data.environment_count || 0} accent="#0F9D77" />
        <Stat icon={Activity} label="Total agent runs" value={data.total_runs || 0} accent="#FF8A3D" />
        <Stat icon={Gauge} label="Overall pass rate" value={`${data.overall_pass_rate || 0}%`} accent="#E5325F" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 card-soft p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading font-semibold text-lg">Regression pass rate</h3>
            <span className="overline text-secondary">Last {data.trend?.length || 0} runs</span>
          </div>
          {data.trend?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3A2DB8" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3A2DB8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(58,45,184,0.08)" vertical={false} />
                <XAxis dataKey="run" stroke="#6B6394" fontSize={11} tickLine={false} />
                <YAxis domain={[0, 100]} stroke="#6B6394" fontSize={11} tickLine={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D6CCF7", borderRadius: 10, color: "#1E1560" }} />
                <Area type="monotone" dataKey="pass_rate" stroke="#3A2DB8" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-secondary text-sm">No runs yet — run an agent to see results.</div>
          )}
        </div>

        <div className="card-soft p-6">
          <h3 className="font-heading font-semibold text-lg mb-6">Latest release readiness</h3>
          {data.latest_run ? (
            <div>
              <div className="flex items-center gap-3 mb-4">
                {data.latest_run.release_ready ? (
                  <><CheckCircle2 className="w-10 h-10 text-[#0F9D77]" />
                    <div><div className="font-heading font-bold text-xl">Release ready</div>
                      <div className="text-xs text-secondary">{data.latest_run.environment_name}</div></div></>
                ) : (
                  <><AlertTriangle className="w-10 h-10 text-[#FF8A3D]" />
                    <div><div className="font-heading font-bold text-xl">Needs attention</div>
                      <div className="text-xs text-secondary">{data.latest_run.environment_name}</div></div></>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-secondary">Pass rate</span><span className="font-mono">{data.latest_run.pass_rate}%</span></div>
                <div className="flex justify-between"><span className="text-secondary">Passed</span><span className="font-mono text-[#0F9D77]">{data.latest_run.passed}</span></div>
                <div className="flex justify-between"><span className="text-secondary">Failed</span><span className="font-mono text-[#E5325F]">{data.latest_run.failed}</span></div>
              </div>
            </div>
          ) : <p className="text-secondary text-sm">No agent runs yet.</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-1 space-y-4"><EmailSettingsCard /><ChangePasswordCard /></div>
        <div className="lg:col-span-2 card-soft">
          <div className="p-6 border-b border-border flex items-center justify-between">
            <h3 className="font-heading font-semibold text-lg">Recent agent runs &amp; reports</h3>
            <span className="text-xs text-secondary">Click a run to view evidence &amp; download the PDF</span>
          </div>
          {data.recent_runs?.length ? (
            <div className="divide-y divide-border">
              {data.recent_runs.map((r) => (
                <button key={r.id} onClick={() => navigate(`/runs/${r.id}`)} className="w-full text-left p-5 flex items-center justify-between hover:bg-muted/60 transition-colors" data-testid={`run-row-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2">
                      {r.status === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#7C3AED]" />}
                      {r.environment_name}
                      {r.trigger === "scheduled" && <Badge className="rounded-full bg-[#7C3AED]/10 text-[#7C3AED] text-[10px]">scheduled</Badge>}
                    </div>
                    <div className="text-xs text-secondary font-mono">{r.started_at?.slice(0, 16).replace("T", " ")} · {r.product}</div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {r.status === "running" ? (
                      <span className="text-xs text-[#7C3AED]">{r.progress?.current || 0}/{r.progress?.total || 0} in progress</span>
                    ) : r.status === "failed" ? (
                      <Badge className="rounded-full bg-[#E5325F]/15 text-[#E5325F]">failed</Badge>
                    ) : (
                      <>
                        <span className="text-sm"><span className="text-[#0F9D77]">{r.passed}✓</span> <span className="text-[#E5325F]">{r.failed}✗</span>{r.blocked ? <span className="text-[#FF8A3D]"> {r.blocked}⊘</span> : null}</span>
                        <Badge className={`rounded-full ${r.release_ready ? "bg-[#0F9D77]/15 text-[#0F9D77]" : "bg-[#FF8A3D]/15 text-[#FF8A3D]"}`}>{r.pass_rate}%</Badge>
                        <FileText className="w-4 h-4 text-[#3A2DB8]" />
                      </>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : <div className="p-8 text-secondary text-sm text-center">No runs yet.</div>}
        </div>
      </div>
    </div>
  );
}
